import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PipelineService from 'services/PipelineService';
import BasicModal from '__components/Modals/presets/BasicModal';
import { getDataQAForInput } from 'components/Inputs/helpers';
import FieldInput from 'components/Fields/FieldInput';
import { getFieldInputValue } from 'utility/getFieldInputValue';
import { getSelectedOptions } from './helpers';

type Option = {
  selected:
    | { value: string; label: string }[]
    | { id: string; label: string }[];
  stageId: string;
  recordId: string;
};

type AddReasonDisqualifiedModalProps = {
  disabled?: boolean;
  handleUpdateFieldOption(data: any): void;
  isRecordsEndpointData?: boolean;
  onConfirm(...args: any[]): void;
  onHide(): void;
  model: any;
  reasonDisqualifiedField: any;
  reasonsDialogData: {
    record: any;
    stageId: string;
  }[];
  show: boolean;
};

const AddReasonDisqualifiedModal = (props: AddReasonDisqualifiedModalProps) => {
  const {
    disabled = false,
    handleUpdateFieldOption,
    onConfirm,
    isRecordsEndpointData = false,
    onHide,
    model,
    reasonDisqualifiedField,
    reasonsDialogData,
    show,
  } = props;
  const { t } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);

  const reasonDisqualifiedFieldRef = useRef(reasonDisqualifiedField);

  useEffect(() => {
    if (reasonDisqualifiedFieldRef.current) {
      if (isRecordsEndpointData) {
        const options = reasonsDialogData.map(({ record, stageId }) => {
          const selected = getFieldInputValue(
            reasonDisqualifiedFieldRef.current,
            record
          ) as { value: string; label: string }[];
          return {
            selected,
            stageId,
            recordId: record.id,
          };
        });
        setSelectedOptions(options);
      } else {
        const options = getSelectedOptions(
          reasonsDialogData,
          reasonDisqualifiedFieldRef.current
        );
        setSelectedOptions(options);
      }
    }
  }, [reasonsDialogData, isRecordsEndpointData]);

  return disabled || !reasonDisqualifiedField ? null : (
    <BasicModal
      buttonText={t('Save')}
      heading={t('Add Reason(s) Disqualified')}
      leftBtn={null}
      onConfirm={() => onConfirm(selectedOptions, reasonDisqualifiedField.id)}
      show={show}
      onHide={onHide}
      size="small"
    >
      {reasonsDialogData.map(({ record, stageId }, i) => {
        return (
          <FieldInput
            key={record.id}
            menuInline
            margin={i !== reasonsDialogData.length - 1}
            label={t('Choose Reason(s) Disqualified') + ' - ' + record.name}
            variant="outline"
            inModal
            disabled={!reasonDisqualifiedField.access.edit}
            field={reasonDisqualifiedField}
            fetchUrl={`custom-objects/${model.id}`}
            value={
              selectedOptions.find(
                (options) =>
                  options.stageId === stageId && options.recordId === record.id
              )?.selected
            }
            onChange={(selected: { id: string; label: string }[]) =>
              setSelectedOptions((prev) =>
                prev.map((data) =>
                  data.stageId === stageId && data.recordId === record.id
                    ? { ...data, selected }
                    : data
                )
              )
            }
            serviceToUse={PipelineService}
            objectId={model.id}
            stages={model.pipeline.stages}
            {...getDataQAForInput(
              `custom-object-record-${reasonDisqualifiedField.name}`,
              reasonDisqualifiedField.fieldType
            )}
            handleUpdateFieldOption={handleUpdateFieldOption}
          />
        );
      })}
    </BasicModal>
  );
};

export default AddReasonDisqualifiedModal;
